import { GameElement } from "../../shared/gameElementTypes";

export type GameStats = {
	moneySpent: number,
	moneyEarned: number,
	immuneCellsBought: Partial<Record<GameElement, number>>,
	experienceBought: number,
	refreshesBought: number,
}

export type RoundMoneyStats = {
	round: number,
	spent: number,
	gained: number,
}

function createEmptyStats(): GameStats {
	return {
		moneySpent: 0,
		moneyEarned: 0,
		immuneCellsBought: {},
		experienceBought: 0,
		refreshesBought: 0,
	};
}

let stats: GameStats = createEmptyStats();
let roundStats = new Map<number, { spent: number, gained: number }>();
let currentRound = 0;
let maxFundsAtRoundStart: number | null = null;

function getCurrentRoundStats() {
	let result = roundStats.get(currentRound);
	if (!result) {
		result = { spent: 0, gained: 0 };
		roundStats.set(currentRound, result);
	}
	return result;
}

function finalizeRound(maxFunds: number) {
	if (maxFundsAtRoundStart === null) return;
	const income = Math.max(0, maxFunds - maxFundsAtRoundStart);
	stats.moneyEarned += income;
	getCurrentRoundStats().gained += income;
	maxFundsAtRoundStart = null;
}

export function resetGameStats() {
	stats = createEmptyStats();
	roundStats = new Map();
	currentRound = 0;
	maxFundsAtRoundStart = null;
}

export function getGameStats() {
	return stats;
}

export function recordSpend(amount: number) {
	stats.moneySpent += amount;
	getCurrentRoundStats().spent += amount;
}

export function recordEarn(amount: number) {
	stats.moneyEarned += amount;
	getCurrentRoundStats().gained += amount;
}

export function recordImmuneCellBought(type: GameElement) {
	stats.immuneCellsBought[type] = (stats.immuneCellsBought[type] || 0) + 1;
}

export function recordExperienceBought() {
	stats.experienceBought += 1;
}

export function recordRefreshBought() {
	stats.refreshesBought += 1;
}

export function beginRound(round: number, maxFunds: number) {
	if (round === currentRound) return;
	finalizeRound(maxFunds);
	currentRound = round;
	maxFundsAtRoundStart = maxFunds;
	getCurrentRoundStats();
}

export function endGame(maxFunds: number) {
	finalizeRound(maxFunds);
}

export function getPerRoundMoneyStats(): RoundMoneyStats[] {
	return Array.from(roundStats.entries())
		.filter(([round]) => round > 0)
		.sort(([left], [right]) => left - right)
		.map(([round, result]) => ({
			round,
			spent: result.spent,
			gained: result.gained,
		}));
}
